import {SearchAbstract} from '@/mixins/SearchAbstract';
import {deepCopyObject} from '@/utils/portuna';
import {type Directive, type DirectiveBinding} from 'vue';
import {isEqual} from '../library/utils';
import ListAbstract from '../mixins/ListAbstract';

class Sort
{
    private readonly _element: HTMLElement;

    private readonly _sortElement: HTMLElement;

    private _bindings: DirectiveBinding;

    private _parent: Nullable<ListAbstract>;

    private _parentSearch: Nullable<SearchAbstract>;

    private _vueInstance: ListAbstract | undefined;

    private _searchInstance: SearchAbstract | undefined;

    private _sortIcons: Record<string, string> = {
        'asc': 'arrow_upward',
        'desc': 'arrow_downward',
    };

    _clickEvent: VoidFunction;

    _keydownEvent: (event: KeyboardEvent) => void;

    _keyupEvent: (event: KeyboardEvent) => void;

    constructor(el: HTMLElement, bindings: DirectiveBinding)
    {
        this._element      = el;
        this._sortElement  = document.createElement('i');
        this._bindings     = Object.assign({}, bindings, {modifiers: deepCopyObject(bindings.modifiers)});
        this._parent       = null;
        this._parentSearch = null;
        this.getVueInstance();
        this.getSearchInstance();

        this._element.tabIndex = 0;
        this._sortElement.classList.add('material-icons');
        this._sortElement.classList.add('md-16');
        this._sortElement.classList.add('sort-status-icon');
        this._sortElement.style.display = 'none';
        this._sortElement.setAttribute('aria-hidden', 'true');
    }

    findParentList()
    {
        if (this._parent === null)
        {
            this._parent = this.findParent('isListAbstract') as ListAbstract;
        }
    }

    findParent(findParameter: string): null | SearchAbstract | ListAbstract
    {
        let nodeVue = null;

        // Find first Vue-Component
        let node: any = this._element;
        while (node && node.tagName !== 'HTML' && node.tagName !== 'BODY' && node.nodeType === 1)
        {
            if (node.__vue__)
            {
                nodeVue = node.__vue__;
                break;
            }

            node = node.parentNode as Node & ParentNode & Element;
        }

        while (nodeVue)
        {
            if (nodeVue[findParameter])
            {
                return nodeVue;
            }
            nodeVue = nodeVue.$parent;
        }

        return null;
    }

    findParentSearch()
    {
        if (this._parentSearch === null)
        {
            this._parentSearch = this.findParent('isSearchAbstract') as SearchAbstract;
        }
    }

    getVueInstance()
    {
        this.findParentList();
        let vueInstance = this._parent;

        // do something on init or instance changed
        if (vueInstance !== this._vueInstance)
        {
            // clear old listener
            if (this._vueInstance)
            {
                // @ts-ignore
                this._vueInstance.emitter.off('setSort');
            }

            this._vueInstance = vueInstance as ListAbstract | undefined;
            // add new listener
            if (this._vueInstance)
            {
                // @ts-ignore
                this._vueInstance.emitter.on('setSort', (field: string, sort: FilterOrder) =>
                {
                    this.setIcon(isEqual(field, this._bindings.value) ? sort : '');
                });
            }
        }

        return this._vueInstance;
    }

    getSearchInstance()
    {
        this.findParentSearch();
        let vueInstance = this._parentSearch;

        // do something on init or instance changed
        if (vueInstance !== this._searchInstance)
        {
            // clear old listener
            if (this._searchInstance)
            {
                // @ts-ignore
                this._searchInstance.emitter.off('setSort');
            }

            this._searchInstance = vueInstance as SearchAbstract | undefined;
            // add new listener
            if (this._searchInstance)
            {
                // @ts-ignore
                this._searchInstance.emitter.on('setSort', (field: string, sort: FilterOrder) =>
                {
                    this.setIcon(isEqual(field, this._bindings.value) ? sort : '');
                });
            }
        }

        return this._searchInstance;
    }

    setIcon(order: FilterOrder | '')
    {
        if (order)
        {
            this._sortElement.style.display = 'inline';
            this._sortElement.innerText     = this._sortIcons[order];
            this._element.setAttribute('aria-sort', 'asc' === order ? 'ascending' : 'descending');
        }
        else
        {
            this._sortElement.innerText     = '';
            this._sortElement.style.display = 'none';
            this._element.removeAttribute('aria-sort');
        }
    }

    clickEvent()
    {
        if (!this._element)
        {
            return;
        }

        let vueInstance: SearchAbstract | undefined = this.getSearchInstance();

        if (vueInstance)
        {
            let order: FilterOrder;
            let field = this._bindings.value;

            // other field - reset sort
            if (!isEqual(vueInstance.sortField, field))
            {
                order = this._bindings.modifiers.desc || 'desc' === this._bindings.arg ? 'desc' : 'asc';
            }
            else
            {
                order = vueInstance.orderBy === 'asc' ? 'desc' : 'asc';
            }

            vueInstance.setSort(field, order);
        }
    }

    keydownEvent(event: KeyboardEvent)
    {
        if ('Space' === event.code)
        {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    keyupEvent(event: KeyboardEvent)
    {
        if ('Space' === event.code || 'Enter' === event.code)
        {
            this.clickEvent();
            event.stopPropagation();
        }
    }

    bind()
    {
        this._clickEvent   = this.clickEvent.bind(this);
        this._keydownEvent = this.keydownEvent.bind(this);
        this._keyupEvent   = this.keyupEvent.bind(this);
        this._element.addEventListener('click', this._clickEvent);
        this._element.addEventListener('keydown', this._keydownEvent);
        this._element.addEventListener('keyup', this._keyupEvent);
        this._element.prepend(this._sortElement);
        this._element.classList.add('pointer');
    }

    unbind()
    {
        this._element.removeEventListener('click', this._clickEvent);
        this._element.removeEventListener('keydown', this._keydownEvent);
        this._element.removeEventListener('keyup', this._keyupEvent);
        this._sortElement.remove();
        // this._element.removeChild(this._sortElement);
    }

    forceModifier(name: 'desc' | 'default', value: boolean)
    {
        this._bindings.modifiers[name] = value;
    }
}

const sort: Directive = {
    created(el, bindings)
    {
        el._vPortunaSort = new Sort(el, bindings);
    },
    mounted(el, bindings)
    {
        window.setTimeout(() =>
        {
            if (el._vPortunaSort)
            {
                el._vPortunaSort.bind();
                if (!el._vPortunaSort.getVueInstance())
                {
                    return;
                }

                if (bindings.arg && ['asc', 'desc'].includes(bindings.arg) && !el._vPortunaSort.getVueInstance().sortField)
                {
                    el._vPortunaSort.forceModifier(bindings.arg, true);
                    el._vPortunaSort.clickEvent();
                }
                else if (isEqual(el._vPortunaSort.getVueInstance().sortField, bindings.value))
                {
                    el._vPortunaSort.setIcon(el._vPortunaSort.getVueInstance().orderBy);
                }
                else if ((bindings.modifiers.default || true === (bindings.arg as boolean | string)) && !el._vPortunaSort.getVueInstance().sortField)
                {
                    el._vPortunaSort.clickEvent();
                }
            }
        }, 0);
    },
    // componentUpdated(el)
    // {
    //     // el._vPortunaSort.getVueInstance();
    // },
    unmounted(el)
    {
        if (el._vPortunaSort)
        {
            el._vPortunaSort.unbind();
            delete el._vPortunaSort;
        }
    },
};

export default sort;